import { useLayoutEffect, useRef, type RefObject } from 'react';

/**
 * Keeps a `.pill-tabs-pill` under the selected tab of a `.pill-tabs` group
 * (transitions.dev "tabs sliding"). The pill snaps into place on first paint
 * and on resize, and tweens — via the CSS on `.pill-tabs-pill` — when the
 * selection moves. A group with no selected tab hides its pill, so the lozenge
 * reads as moving between groups when the active route crosses a capsule.
 *
 * `activeKey` is whatever identifies the selection (route, tab id); the hook
 * re-measures whenever it changes.
 */
export function useSlidingPill(groupRef: RefObject<HTMLElement | null>, activeKey: string | null) {
  const firstPaint = useRef(true);

  useLayoutEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    const pill = group.querySelector<HTMLElement>(':scope > .pill-tabs-pill');
    if (!pill) return;

    const place = (animate: boolean) => {
      const tab = group.querySelector<HTMLElement>('[aria-selected="true"]');
      if (!tab) {
        pill.style.opacity = '0';
        return;
      }
      const wasHidden = pill.style.opacity !== '1';
      const write = () => {
        pill.style.opacity = '1';
        pill.style.transform = `translateX(${tab.offsetLeft}px)`;
        pill.style.width = `${tab.offsetWidth}px`;
        pill.style.height = `${tab.offsetHeight}px`;
        pill.style.top = `${tab.offsetTop}px`;
      };
      // Coming from hidden, the pill fades in where it belongs rather than
      // sliding in from wherever it last was.
      if (animate && !wasHidden) {
        write();
        return;
      }
      const prev = pill.style.transition;
      pill.style.transition = wasHidden && animate ? 'opacity var(--tabs-dur) var(--tabs-ease)' : 'none';
      write();
      void pill.offsetWidth;
      pill.style.transition = prev;
    };

    place(!firstPaint.current);
    firstPaint.current = false;

    // jsdom (the tests) has no ResizeObserver; the pill still lands on first paint.
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => place(false));
    ro.observe(group);
    return () => ro.disconnect();
  }, [groupRef, activeKey]);
}
